import React, { useEffect, useState } from 'react';
import { dataService } from '../../services/dataService';
import { ConsultationPhoto } from '../../types/domain';
import ConsultationPhotoGallery from './ConsultationPhotoGallery';

/**
 * FR-17 on the doctor dashboard: the selected patient's consultation photos,
 * read-only. Collapsed by default and loaded only when opened, because signing
 * a URL per photo is wasted work for the many patient selections that are
 * about glucose, not photos.
 *
 * Existing photos stay visible even when the admin has switched uploads off —
 * the switch stops new uploads; it does not hide a child's records from their
 * doctor.
 */
const PatientConsultationPhotos: React.FC<{ patientId: string }> = ({ patientId }) => {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState<ConsultationPhoto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A different patient starts closed and unloaded, or the previous child's
  // photos would show under the new name.
  useEffect(() => {
    setOpen(false);
    setPhotos(null);
    setError(null);
  }, [patientId]);

  useEffect(() => {
    if (!open || photos !== null) {
      return undefined;
    }
    let cancelled = false;
    dataService
      .listConsultationPhotos(patientId)
      .then(result => {
        if (!cancelled) setPhotos(result);
      })
      .catch(() => {
        if (!cancelled) setError('Gagal memuat foto konsultasi.');
      });
    return () => {
      cancelled = true;
    };
  }, [open, photos, patientId]);

  return (
    <details
      className="patient-targets"
      open={open}
      onToggle={e => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary>Foto konsultasi</summary>
      {error ? (
        <div className="target-band-error">{error}</div>
      ) : photos === null ? (
        <div className="text-sm">Memuat foto...</div>
      ) : (
        <ConsultationPhotoGallery
          photos={photos}
          emptyText="Pasien ini belum mengunggah foto konsultasi."
        />
      )}
    </details>
  );
};

export default PatientConsultationPhotos;
